import { Reducer } from 'react';

import type { PanelRef } from './useSampleTableGrid';

/**
 * State for the sample table grid.
 */
export interface SampleTableGridState {
  isCollapsed: boolean;
  pageSize: number;
  totalCount: number | null;
  firstDataRendered: boolean;
}

/**
 * Actions for the sample table grid reducer.
 */
export type SampleTableGridAction =
  | { type: 'TOGGLE_COLLAPSE' }
  | { type: 'SET_COLLAPSED'; value: boolean }
  | { type: 'SET_PAGE_SIZE'; value: number }
  | { type: 'SET_TOTAL_COUNT'; value: number | null }
  | { type: 'SET_FIRST_DATA_RENDERED'; value: boolean };

/**
 * Returns the initial state for the sample table grid.
 */
export function getInitialState(
  panelRef: PanelRef,
  studyId: string,
  paginationPageSizeKeyFn: (studyId: string) => string,
  defaultPageSize: number,
): SampleTableGridState {
  const saved = localStorage.getItem(paginationPageSizeKeyFn(studyId));
  const parsedPageSize = saved ? Number.parseInt(saved, 10) : defaultPageSize;
  const pageSize = Number.isNaN(parsedPageSize) ? defaultPageSize : parsedPageSize;

  return {
    isCollapsed: panelRef.current?.isCollapsed?.() ?? false,
    pageSize,
    totalCount: null,
    firstDataRendered: false,
  };
}

/**
 * Reducer for the sample table grid state.
 */
export const sampleTableGridReducer: Reducer<SampleTableGridState, SampleTableGridAction> = (state, action) => {
  switch (action.type) {
    case 'TOGGLE_COLLAPSE':
      return { ...state, isCollapsed: !state.isCollapsed };
    case 'SET_COLLAPSED':
      return { ...state, isCollapsed: action.value };
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.value };
    case 'SET_TOTAL_COUNT':
      return { ...state, totalCount: action.value };
    case 'SET_FIRST_DATA_RENDERED':
      return { ...state, firstDataRendered: action.value };
    default:
      return state;
  }
};
